import axios from "axios";
import { BASE_URL } from "../config";
import authStorage from "./authStorage";

/**
 * Admin feature switches, read once per page load.
 *
 * THE KEYS ARE A CONTRACT with `api-contingentfitness/helpers/featureFlags.js`.
 * Renaming one on either side silently hides the module, because an unknown key
 * is simply absent from the map.
 */
export const FEATURE = {
  LEADERBOARD: "leaderboard",
  MY_PERFORMANCE: "myPerformance",
  SAMSUNG_CONNECT: "samsungConnect",
};

/**
 * ⚠️ AN UNKNOWN OR UNFETCHED FLAG READS AS ENABLED.
 *
 * Same default as the server. It means a slow or failed flag request shows the
 * app as it was rather than blanking every gated module, which is the far worse
 * failure: a member would see navigation silently missing and assume the product
 * was broken. Only an explicit `false` hides anything.
 */
const DEFAULT_WHEN_UNKNOWN = true;

let cache = null;
let inFlight = null;

/**
 * Fetches the flag map, de-duplicating concurrent callers.
 *
 * Cached for the page's lifetime: several components ask on mount and the answer
 * cannot change without an admin saving on another screen, which reloads anyway.
 * `refresh()` exists for the Settings screen, which changes them itself.
 */
export const loadFeatureFlags = async () => {
  if (cache) return cache;
  if (inFlight) return inFlight;

  inFlight = axios({
    url: `${BASE_URL}/settings/features`,
    method: "GET",
    headers: { "x-access-token": authStorage.getItem("auth-token") },
  })
    .then((res) => {
      cache = res?.data?.data || {};
      return cache;
    })
    .catch(() => {
      // Fail open - see the note above.
      cache = {};
      return cache;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
};

/** Drops the cache so the next read hits the server. Used after an admin saves. */
export const refreshFeatureFlags = () => {
  cache = null;
};

/** Synchronous read of the already-loaded map. Unknown -> enabled. */
export const isFeatureEnabled = (key, flags) => {
  const map = flags || cache;
  if (!map || !(key in map)) return DEFAULT_WHEN_UNKNOWN;
  return map[key] !== false;
};

export default { FEATURE, loadFeatureFlags, refreshFeatureFlags, isFeatureEnabled };
